import { useCallback, useEffect, useState } from 'react';
import { ui, cn } from '../../lib/ui';
import { customDomainError, domainStatus } from '../../lib/sites';
import * as sitesApi from '../../api/sites';
import DnsRecordsTable from '../DnsRecordsTable';

/**
 * Custom domains for the uploaded site (docs/Decisions.md D45). The org proves
 * it controls the domain with a TXT record, then points the domain at the
 * CNAME target CloudFront gives it; the domain goes live once its certificate
 * is issued. Org admins and platform admins only; premium.
 */

const TONE = {
  success: ui.pillSuccess,
  danger: ui.pillDanger,
  info: ui.pillInfo,
  neutral: ui.pillNeutral,
};

function DnsRecords({ records }) {
  if (!records?.length) return null;
  return (
    <div className="mt-3">
      <DnsRecordsTable records={records} />
    </div>
  );
}

export default function SiteDomains({ canUpload, canManage, hostingConfigured }) {
  const [domains, setDomains] = useState([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const enabled = canUpload && canManage && hostingConfigured;

  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await sitesApi.listSiteDomains();
      setDomains(data.domains || []);
      setError(null);
    } catch (err) {
      if (err.statusCode === 503) setUnavailable(true);
      else setError(err.message);
    }
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  async function run(label, fn, success) {
    setBusy(label);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (success) setNotice(success);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
      await load();
    }
  }

  async function add(e) {
    e.preventDefault();
    const problem = customDomainError(input);
    if (problem) {
      setError(problem);
      return;
    }
    await run(
      'add',
      async () => {
        await sitesApi.addSiteDomain(input.trim());
        setInput('');
      },
      'Added. Publish the TXT record shown below, then choose Verify.'
    );
  }

  if (!canUpload || !canManage || !hostingConfigured) return null;

  return (
    <section className={ui.card} aria-labelledby="site-domains-title">
      <div className={ui.row}>
        <h2 id="site-domains-title" className={cn(ui.h2, 'mb-0!')}>
          Custom domains
        </h2>
        <span className={ui.pillPremium}>Premium</span>
      </div>
      <p className="mt-3 text-body text-dcon-ink-body">
        Serve your uploaded site at your organization&apos;s own domain. Each domain gets its own
        certificate, issued by Amazon once you prove you control the domain.
      </p>

      {unavailable ? (
        <p className={cn(ui.alertInfo, 'mt-3')}>
          Custom domains are not set up in this environment yet.
        </p>
      ) : (
        <>
          {error ? (
            <p className={cn(ui.alertError, 'mt-3')} role="alert">
              {error}
            </p>
          ) : null}
          {notice ? (
            <p className={cn(ui.alertSuccess, 'mt-3')} role="status">
              {notice}
            </p>
          ) : null}

          <form className={cn(ui.row, 'mt-3 items-end!')} onSubmit={add}>
            <label className={cn(ui.field, 'mb-0! min-w-64 flex-1')}>
              <span className={ui.label}>Domain</span>
              <input
                className={ui.input}
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="www.example.org"
                autoComplete="off"
                spellCheck={false}
              />
            </label>
            <button type="submit" className={ui.btnPrimary} disabled={busy === 'add'}>
              Add domain
            </button>
          </form>

          {domains.length ? (
            <ul className="mt-4 flex flex-col gap-4">
              {domains.map(d => {
                const status = domainStatus(d.status);
                return (
                  <li key={d.domain} className={ui.cardFlat}>
                    <div className={cn(ui.row, ui.spread)}>
                      <div className={ui.row}>
                        <span className="font-mono text-data">{d.domain}</span>
                        <span className={TONE[status.tone] || ui.pillNeutral}>{status.label}</span>
                      </div>
                      <div className={ui.row}>
                        {d.url ? (
                          <a
                            className={cn(ui.btnSecondary, ui.btnSm)}
                            href={d.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open
                          </a>
                        ) : null}
                        {status.action === 'verify' ? (
                          <button
                            type="button"
                            className={cn(ui.btnPrimary, ui.btnSm)}
                            disabled={busy === d.domain}
                            onClick={() =>
                              run(
                                d.domain,
                                () => sitesApi.verifySiteDomain(d.domain),
                                'Verified. Now point the domain at the CNAME target.'
                              )
                            }
                          >
                            Verify
                          </button>
                        ) : null}
                        {status.action === 'refresh' ? (
                          <button
                            type="button"
                            className={cn(ui.btnSecondary, ui.btnSm)}
                            disabled={busy === d.domain}
                            onClick={() =>
                              run(d.domain, () => sitesApi.refreshSiteDomain(d.domain))
                            }
                          >
                            Check status
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className={cn(ui.btnGhost, ui.btnSm)}
                          disabled={busy === d.domain}
                          onClick={() => run(d.domain, () => sitesApi.removeSiteDomain(d.domain))}
                          aria-label={`Remove ${d.domain}`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    {status.next ? <p className={cn(ui.hint, 'mt-2')}>{status.next}</p> : null}
                    {d.lastError && d.status !== 'active' ? (
                      <p className={cn(ui.hint, 'mt-1')}>Last check: {d.lastError}</p>
                    ) : null}
                    <DnsRecords records={d.dnsRecords} />
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className={cn(ui.hint, 'mt-3')}>No custom domains yet.</p>
          )}
        </>
      )}
    </section>
  );
}
